/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useTomraDrops } from "../../store/useTomraDrops";
import { useDropLocations } from "../../store/useDropLocations";
import { Tooltip } from "@material-tailwind/react";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  MaterialsChartFilters,
  getDefaultMaterialsChartFilters,
  type MaterialsChartFiltersState,
} from "../../pages/Volumes/MaterialsChartFilters";
import {
  getStoreColor,
  getCityColor,
  TOTAL_CONTRAST_COLOR,
} from "../../constants/plasticColors";

dayjs.extend(isoWeek);

interface StatieItemsProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const StatieItems = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: StatieItemsProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
    const { statieItemsPerLocation, getStatieItemsPerLocation } =
      useTomraDrops();
  const { dropLocations, getDropLocations } = useDropLocations();

  const [_, takeScreenshot] = useScreenshot();
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  /** State */
    const [filters, setFilters] = useState<MaterialsChartFiltersState>(() =>
      getDefaultMaterialsChartFilters()
    );

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      // Create a link to download the image
      const link = document.createElement("a");
      link.href = image;
      link.download = "volumes_and_revenue.png"; // Set the default file name
      link.click(); // Programmatically click the link to trigger download
    });
  };

  const monthlyTickAmount = useMemo(() => {
    return statieItemsPerLocation?.length
      ? statieItemsPerLocation?.[0]?.data?.length - 1
      : 11;
  }, [statieItemsPerLocation]);

    /** Use API response directly - backend handles filtering when !fetchAllMode; frontend filters when fetchAllMode */
    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";
    const filteredStatieItemsData = useMemo(() => {
      if (!useGeneralFilter) return statieItemsPerLocation ?? [];
      const raw = statieItemsPerLocation ?? [];
      if (
        (filterType ?? "location") === "location" &&
        (filterPlaces ?? []).length === 0
      )
        return [];
      const realFilterPlaces =
        filterPlaces?.filter((p) => p !== TOTAL_ALL_STORES_ID) ?? [];
      if (!realFilterPlaces.length) return raw;
      const selectedNames = new Set<string>();
      if ((filterType ?? "location") === "city") {
        realFilterPlaces.forEach((p) => selectedNames.add(p));
      } else {
        realFilterPlaces.forEach((id) => {
          const loc = dropLocations?.find((l) => l._id === id);
          if (loc?.cost_place) selectedNames.add(loc.cost_place);
        });
      }
      return raw.filter((s) => selectedNames.has(s.name));
    }, [
      statieItemsPerLocation,
      useGeneralFilter,
      filterType,
      filterPlaces,
      dropLocations,
    ]);

    const includeTotalAllStores =
      useGeneralFilter && (filterPlaces ?? []).includes(TOTAL_ALL_STORES_ID);

    const chartDataWithTotal = useMemo(() => {
      const base = filteredStatieItemsData ?? [];
      if (!includeTotalAllStores || base.length === 0) return base;
      const firstSeries = base[0];
      const data = firstSeries?.data;
      if (!data || !Array.isArray(data)) return base;
      const totalData = data.map(([x]: [number, number], idx: number) => {
        const sum = base.reduce(
          (acc, s) => acc + (Number(s?.data?.[idx]?.[1] ?? 0) || 0),
          0
        );
        return [x, sum];
      });
      return [...base, { name: "Total all stores", data: totalData }];
    }, [filteredStatieItemsData, includeTotalAllStores]);

    const locationSeriesNames = useMemo(
      () => (chartDataWithTotal ?? []).map((series) => series?.name || ""),
      [chartDataWithTotal]
    );
    const locationSeriesColors = useMemo(() => {
      const isCityView =
        useGeneralFilter && (filterType ?? "location") === "city";
      return locationSeriesNames.map((name) =>
        name === "Total all stores"
          ? TOTAL_CONTRAST_COLOR
          : isCityView
            ? getCityColor(name)
            : getStoreColor(name)
      );
    }, [locationSeriesNames, useGeneralFilter, filterType]);

  /** Effects */
  useEffect(() => {
    getDropLocations();
  }, []);

    const prevFilterSigRef = useRef<string | null>(null);

  useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({ filters, filterType });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      const typeParam = useGeneralFilter
        ? (filterType ?? "location")
        : undefined;
      const placesParam = undefined; // Always fetch all; frontend filters by filterPlaces
      const {
        period: p,
        fromYear,
        toYear,
        fromMonth,
        toMonth,
        fromQuarter,
        toQuarter,
        fromWeek,
        toWeek,
      } = filters;
      const promise =
        p === "week"
          ? getStatieItemsPerLocation(
              fromYear,
              fromWeek,
              toYear,
              toWeek,
              "weekly",
              typeParam,
              placesParam
            )
          : (() => {
              let startMonth: number;
              let endMonth: number;
              if (p === "month") {
                startMonth = fromMonth;
                endMonth = toMonth;
              } else if (p === "quarter") {
                startMonth = (fromQuarter - 1) * 3 + 1;
                endMonth = toQuarter * 3;
              } else {
                startMonth = 1;
                endMonth = 12;
              }
              return getStatieItemsPerLocation(
                fromYear,
        startMonth,
                toYear,
        endMonth,
                "monthly",
                typeParam,
                placesParam
              );
            })();
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
      );
    }
  }, [
      filters,
    getStatieItemsPerLocation,
      useGeneralFilter,
      filterType,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const queryParams = new URLSearchParams({
        startYear: filters.fromYear.toString(),
        endYear: filters.toYear.toString(),
        period: "daily",
      });

      if (filters.period === "month") {
        queryParams.append("startMonth", filters.fromMonth.toString());
        queryParams.append("endMonth", filters.toMonth.toString());
      } else if (filters.period === "quarter") {
        queryParams.append(
          "startMonth",
          ((filters.fromQuarter - 1) * 3 + 1).toString()
        );
        queryParams.append("endMonth", (filters.toQuarter * 3).toString());
      } else if (filters.period === "year") {
        queryParams.append("startMonth", "1");
        queryParams.append("endMonth", "12");
      } else {
        const startDate = dayjs()
          .year(filters.fromYear)
          .isoWeek(filters.fromWeek)
          .startOf("isoWeek");
        const endDate = dayjs()
          .year(filters.toYear)
          .isoWeek(filters.toWeek)
          .endOf("isoWeek");
        queryParams.append("startYear", startDate.year().toString());
        queryParams.append("startMonth", (startDate.month() + 1).toString());
        queryParams.append("endYear", endDate.year().toString());
        queryParams.append("endMonth", (endDate.month() + 1).toString());
      }

      if (useGeneralFilter) {
        queryParams.append("type", filterType ?? "location");
        const realPlaces =
          (filterPlaces ?? []).filter((p) => p !== TOTAL_ALL_STORES_ID) ?? [];
        if (realPlaces.length) {
          queryParams.append("places", realPlaces.join(","));
        }
      }

      axios
        .get(`/tomra-drops/stats/statie-items-per-location?${queryParams.toString()}`)
        .then((response) => {
          let series = (response.data as { name: string; data: [number, number][] }[]) ?? [];

          if (
            filters.period === "week" &&
            series.length > 0
          ) {
            const startTs = dayjs()
              .year(filters.fromYear)
              .isoWeek(filters.fromWeek)
              .startOf("isoWeek")
              .valueOf();
            const endTs = dayjs()
              .year(filters.toYear)
              .isoWeek(filters.toWeek)
              .endOf("isoWeek")
              .valueOf();
            series = series.map((item) => ({
              ...item,
              data: item.data.filter(([ts]) => ts >= startTs && ts <= endTs),
            }));
          }

          if (
            useGeneralFilter &&
            (filterPlaces ?? []).includes(TOTAL_ALL_STORES_ID) &&
            series.length > 0
          ) {
            const totalData = series[0].data.map(([x], idx) => {
              const sum = series.reduce(
                (acc, s) => acc + (Number(s?.data?.[idx]?.[1] ?? 0) || 0),
                0
              );
              return [x, sum] as [number, number];
            });
            series = [...series, { name: "Total all stores", data: totalData }];
          }

          const allTs = new Set<number>();
          series.forEach((s) => {
            const d = (s.data as [number, number][] | undefined) ?? [];
            d.forEach(([ts]) => allTs.add(ts));
          });
          const sortedTs = Array.from(allTs).sort((a, b) => a - b);

          const header =
            "Date," +
            series
              .map((s) => String(s?.name ?? "").replace(/,/g, " "))
              .join(",") +
            "\n";

          const rows = sortedTs.map((ts) => {
            const values = series.map((s) => {
              const pair = (s.data ?? []).find(([t]) => t === ts);
              return pair != null ? String(pair[1] ?? "") : "";
            });

            return [dayjs(ts).format("YYYY-MM-DD"), ...values].join(",");
          });

          const csv = header + rows.join("\n");
          const blob = new Blob([csv], {
            type: "text/csv;charset=utf-8;",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `statie_items_daily_${dayjs().format("YYYY-MM-DD")}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error downloading CSV for StatieItems:", error);
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

  return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            <MaterialsChartFilters
              state={filters}
              onChange={setFilters}
              hasData={!!(chartDataWithTotal && chartDataWithTotal.length > 0)}
              onDownloadImage={downloadScreenshot}
              onDownloadCsv={downloadCsv}
              isCsvDownloading={isCsvDownloading}
            />
          </div>
        </div>
        <div>
          <section ref={snapshotRef} className="mt-4">
        <div className="flex items-center mb-2 sm:mb-12">
          <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
                Statie (items/
                {filters.period.charAt(0).toLowerCase() +
                  filters.period.slice(1).toLowerCase()}
                )
          </p>
          <Tooltip
            content="Non-cumulative: Shows statiegeld items for the selected date range"
            placeholder={undefined}
          >
            <img
              src={InformationIcon}
              alt="info"
              className="inline-block w-4 h-4 ml-2 cursor-pointer"
            />
          </Tooltip>
        </div>
        {/* @ts-ignore */}
            {chartDataWithTotal && chartDataWithTotal.length > 0 ? (
          <Chart
            options={{
              ...options,
                  colors:
                    locationSeriesColors.length > 0
                      ? locationSeriesColors
                      : options.colors,
              xaxis: {
                // Only show 1 tick when there's only one month
                tickAmount: monthlyTickAmount,
                title: {
                      text: filters.period === "week" ? "Week" : "Month",
                  style: {
                    fontSize: "14px",
                    fontWeight: "bold",
                  },
                },
                labels: {
                  // @ts-ignore
                  formatter: function (value: number) {
                        if (filters.period === "week") {
                          // Use the timestamp's year to correctly calculate ISO week
                          // This ensures correct week numbers at year boundaries
                          const date = dayjs(value);
                          const weekNumber = date.isoWeek();
                          return `${weekNumber}`;
                    }
                    return dayjs(value).format("MMM");
                      },
                    },
                  },
                }}
                series={chartDataWithTotal}
            // type="line"
            height={300}
          />
        ) : (
          <div className="flex items-center justify-center h-[300px] w-full">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-base"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        )}
          </section>
        </div>
      </div>
    );
  }
  );

const options = {
  chart: {
    type: "line",
    zoom: {
      enabled: false,
    },
    toolbar: {
      show: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  grid: {
    strokeDashArray: 5,
  },
  markers: {
    size: 1,
  },
  legend: {
    show: true,
    position: "right",
  },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
      // Create an array of series with their values
      const seriesData = series.map((seriesValues: any, index: number) => ({
        name: w.globals.seriesNames[index],
        value: seriesValues[dataPointIndex],
        color: w.config.colors[index],
      }));

      // Sort by value in descending order (highest to lowest)
      seriesData.sort((a: any, b: any) => b.value - a.value);

      // Build the tooltip HTML with original ApexCharts styling
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';

      // Format date based on the x-axis value
      const xValue = w.globals.seriesX[seriesIndex][dataPointIndex];
      const isWeekly = w.config.xaxis.title.text === "Week";
      if (isWeekly) {
        tooltipHtml += `Week ${dayjs(xValue).isoWeek()} ${dayjs(xValue).year()}</div>`;
      } else {
        tooltipHtml += `${dayjs(xValue).format("MMM YYYY")}</div>`;
      }

      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(item.value);

        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${
              item.color
            };"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${
                  item.name
                }: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });

      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "Statie Items",
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    labels: {
      formatter: function (value: number) {
        return new Intl.NumberFormat("nl-NL", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      },
    },
  },
  colors: [
    "#e34e76",
    "#007887",
    "#8773aa",
    "#e6e1ff",
    "#fe3e3e",
    "#ffff78",
    "#d2e6ff",
    "#d2d2b2",
    "#5fd0ae",
    "#ff8dfa",
    "#ff6e41",
    "#808080",
  ],
};
